import { useCallback, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setPinnedAgentIds } from '../../../redux/features/users/usersSlice';
import { acquireApiAccessTokenAsync, acquireNexusApiAccessTokenAsync } from '../../../services/auth/AuthService';
import { useProviderServices } from '../../../app/providers/AppServicesContext';
import { alerts } from '../../../shared/ui/Alerts';
import { ChatProviderType } from '../../../domain/chat/models/ChatProviderType';
import { createChatSessionHandler } from '../../../application/chat-sessions/handlers/CreateChatSessionHandler';
import { setDocuments } from '../../../redux/features/chatDocuments/chatDocumentsSlice';
import { upsertSession } from '../../../redux/features/chatSessions/chatSessionsSlice';
import { setSelectedSessionId } from '../../../redux/features/chatUi/chatUiSlice';
import { isNexusAgent } from '../../utils/AgentUtils';

import {
    RecentlyUsedAgentVm,
    selectRecentlyUsedAgents,
} from '../../../domain/selectors/view-models/chat/selectRecentlyUsedAgents';
import { selectActiveChatSessionCount } from '../../../domain/selectors/base/chat/selectActiveChatSessionCount';
const MAX_PINNED_AGENTS = 4;

const createChatSessionToastMessages = {
    pending: 'Creating chat...',
    success: 'Chat created successfully!',
    error: 'Failed to create a new chat. Please try again.',
};

export interface UsePinnedAgentsVm {
    pinnedAgentIds: string[];
    pinnedSet: Set<string>;
    isPinned: (agentId: string) => boolean;
    togglePin: (agentId: string) => void;
    maxPinnedAgents: number;
}

export const usePinnedAgents = (): UsePinnedAgentsVm => {
    const dispatch = useAppDispatch();
    const { instance } = useMsal();
    const providerServices = useProviderServices();
    const pinnedAgentIds = useAppSelector((state) => state.users.pinnedAgentIds);
    const userId = useAppSelector((state) => state.app.activeUserInfo?.id) ?? '';

    const pinnedSet = useMemo(() => new Set(pinnedAgentIds), [pinnedAgentIds]);

    const togglePin = useCallback(
        (agentId: string) => {
            let nextPinnedIds: string[];

            if (pinnedSet.has(agentId)) {
                nextPinnedIds = pinnedAgentIds.filter((id) => id !== agentId);
            } else {
                if (pinnedAgentIds.length >= MAX_PINNED_AGENTS) {
                    alerts.error(`You can only pin up to ${MAX_PINNED_AGENTS} agents!`);
                    return;
                }

                nextPinnedIds = [...pinnedAgentIds, agentId];
            }

            dispatch(setPinnedAgentIds(nextPinnedIds));

            void (async () => {
                try {
                    const accessToken = await acquireApiAccessTokenAsync({
                        msalInstance: instance,
                    });
                    const command = { userId, pinnedAgentIds: nextPinnedIds };
                    await providerServices.proGPT.users.updatePinnedAgentsAsync(userId, command, accessToken);
                } catch (error) {
                    console.error('[togglePin] Failed to persist pinned agents:', error);
                    dispatch(setPinnedAgentIds(pinnedAgentIds));
                }
            })();
        },
        [dispatch, instance, pinnedAgentIds, pinnedSet, providerServices, userId],
    );

    const isPinned = useCallback(
        (agentId: string) => {
            return pinnedSet.has(agentId);
        },
        [pinnedSet],
    );

    return {
        pinnedAgentIds,
        pinnedSet,
        isPinned,
        togglePin,
        maxPinnedAgents: MAX_PINNED_AGENTS,
    };
};

export interface RecentlyUsedAgentItemVm extends RecentlyUsedAgentVm {
    isPinned: boolean;
}

export interface UseRecentlyUsedAgentsVm {
    agents: RecentlyUsedAgentItemVm[];
    isMaxChatsReached: boolean;
    togglePin: (agentId: string) => void;
    createAgentChat: (agentId: string) => Promise<void>;
}

export const useRecentlyUsedAgents = (): UseRecentlyUsedAgentsVm => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { instance } = useMsal();
    const providerServices = useProviderServices();
    const recentAgents = useAppSelector(selectRecentlyUsedAgents);
    const agentsById = useAppSelector((state) => state.agents.byId);
    const chatCount = useAppSelector(selectActiveChatSessionCount);
    const { pinnedSet, togglePin } = usePinnedAgents();

    const allowedChatCount = useMemo(() => {
        const parsedValue = Number(process.env.REACT_APP_CHAT_SESSION_COUNT ?? '');

        if (!Number.isFinite(parsedValue) || parsedValue <= 0) {
            return null;
        }

        return parsedValue;
    }, []);

    const isMaxChatsReached = allowedChatCount !== null && chatCount >= allowedChatCount;

    const createAgentChat = useCallback(
        async (agentId: string): Promise<void> => {
            const agent = agentsById[agentId];

            if (!agent || !isNexusAgent(agent)) {
                return;
            }

            if (allowedChatCount !== null && chatCount >= allowedChatCount) {
                alerts.error(
                    `You have reached the maximum limit of ${allowedChatCount} active chat sessions. Please delete an existing chat to create a new one.`,
                );
                return;
            }

            try {
                const accessToken = await acquireNexusApiAccessTokenAsync({
                    msalInstance: instance,
                });

                const createdSession = await alerts.promise(
                    createChatSessionHandler({
                        request: {
                            providerType: ChatProviderType.Nexus,
                            agentId: agent.agentId,
                            title: `${agent.name} Chat`,
                            enableFileSearch: agent.allowFileSearchAtConversationLevel ?? false,
                            enableCodeInterpreter: agent.allowCodeInterpreterAtConversationLevel ?? false,
                        },
                        accessToken,
                        providerServices,
                    }),
                    createChatSessionToastMessages,
                );

                dispatch(upsertSession(createdSession.session));
                dispatch(
                    setDocuments({
                        sessionId: createdSession.session.id,
                        documents: createdSession.documents,
                    }),
                );
                dispatch(setSelectedSessionId(createdSession.session.id));
                navigate(`/chat/${createdSession.session.id}`);
            } catch {
                return;
            }
        },
        [agentsById, allowedChatCount, chatCount, dispatch, instance, navigate, providerServices],
    );

    // Sort: pinned agents first, then the rest in their original (most-recent) order.
    const agents: RecentlyUsedAgentItemVm[] = useMemo(() => {
        const items = recentAgents.map((a) => ({
            ...a,
            isPinned: pinnedSet.has(a.agentId),
        }));

        items.sort((a, b) => {
            if (a.isPinned && !b.isPinned) return -1;
            if (!a.isPinned && b.isPinned) return 1;
            return 0;
        });

        return items;
    }, [recentAgents, pinnedSet]);

    return { agents, isMaxChatsReached, togglePin, createAgentChat };
};

export default useRecentlyUsedAgents;
